import { IndicatorType } from '@shared/types';
import React, { createContext, useContext, useMemo } from 'react';
import { filter, Observable } from 'rxjs';
import {
  CandlestickUpdate,
  NewCandlestickEvent
} from '../services/candlestick-update.service';
import { IndicatorUpdate } from '../services/indicator-update.service';
import { useChartUpdateStreamContext } from './chart-update-stream.context';
import { DataFeedResponse } from '@server/data-feed/schemas/output.schema';
import { TradingFormState } from '@client/widgets/trading-form/trading-form.types';

export type ChartStreamContextValue = {
  timeframeIndex: number;
  formState: TradingFormState;
  feedResponse$: Observable<DataFeedResponse | undefined>;
  indicatorUpdate$: Observable<IndicatorUpdate>;
  candlestickUpdate$: Observable<CandlestickUpdate>;
  newCandlestick$: Observable<NewCandlestickEvent>;
  candlestickData$: Observable<CandlestickUpdate['data']>;
  getLatestCandlesticks: () => CandlestickUpdate['data'];
  getIndicatorSnapshots: (indicatorType: IndicatorType) => IndicatorUpdate[];
  getCandlestickSnapshots: () => CandlestickUpdate[];
};

const ChartStreamContext = createContext<ChartStreamContextValue | undefined>(
  undefined
);

type ChartStreamProviderProps = {
  timeframeIndex: number;
  formState: TradingFormState;
  children: React.ReactNode;
};

export const ChartStreamProvider = ({
  timeframeIndex,
  formState,
  children
}: ChartStreamProviderProps) => {
  const chartUpdateStream = useChartUpdateStreamContext();

  const value = useMemo<ChartStreamContextValue>(() => {
    const indicatorUpdate$ = chartUpdateStream
      .getIndicatorUpdate$()
      .pipe(filter((update) => update.timeframeIndex === timeframeIndex));

    const candlestickUpdate$ = chartUpdateStream
      .getCandlestickUpdate$()
      .pipe(filter((update) => update.timeframeIndex === timeframeIndex));

    const newCandlestick$ = chartUpdateStream
      .getNewCandlestick$()
      .pipe(filter((event) => event.timeframeIndex === timeframeIndex));

    const candlestickData$ =
      chartUpdateStream.getCandlestickData$(timeframeIndex);
    const feedResponse$ = chartUpdateStream.getFeedResponse$();

    return {
      timeframeIndex,
      formState,
      feedResponse$,
      indicatorUpdate$,
      candlestickUpdate$,
      newCandlestick$,
      candlestickData$,
      getLatestCandlesticks: () =>
        chartUpdateStream.getLatestCandlesticks(timeframeIndex),
      getIndicatorSnapshots: (indicatorType: IndicatorType) =>
        chartUpdateStream.getIndicatorSnapshots(timeframeIndex, indicatorType),
      getCandlestickSnapshots: () =>
        chartUpdateStream.getCandlestickSnapshots(timeframeIndex)
    };
  }, [chartUpdateStream, formState, timeframeIndex]);

  return (
    <ChartStreamContext.Provider value={value}>
      {children}
    </ChartStreamContext.Provider>
  );
};

export const useChartStreamContext = () => {
  const context = useContext(ChartStreamContext);

  if (!context) {
    throw new Error(
      'useChartStreamContext must be used within ChartStreamProvider'
    );
  }

  return context;
};
